import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  TextField,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import api from "../../api";

function formatWhen(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusColor(status) {
  if (status === "queued_for_human") return "warning";
  if (status === "assigned") return "success";
  if (status === "closed") return "default";
  return "info";
}

export default function SupportChatInbox() {
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [hours, setHours] = useState(null);
  const [error, setError] = useState("");
  const pollInFlightRef = useRef(false);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  const loadList = async (opts = { initial: false }) => {
    if (pollInFlightRef.current && !opts.initial) {
      return;
    }
    pollInFlightRef.current = true;
    if (opts.initial) {
      setLoadingList(true);
    } else {
      setRefreshing(true);
    }
    try {
      // First try open conversations; if empty, fallback to all statuses.
      let res = await api.get("admin/support-chat/conversations", {
        params: { status: "open" },
        timeout: 10000,
      });
      let payload = res?.data?.data || [];
      if (Array.isArray(payload) && payload.length === 0) {
        const fallback = await api.get("admin/support-chat/conversations", {
          timeout: 10000,
        });
        res = fallback;
        payload = fallback?.data?.data || [];
      }
      setConversations(payload);
      setHours(res?.data?.support_hours || null);
      if (payload.length > 0 && !selectedId) {
        setSelectedId(payload[0].id);
      } else if (payload.length === 0) {
        setSelectedId(null);
        setDetail(null);
      }
      // Clear stale error banner after a successful refresh.
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load support chats.");
    } finally {
      pollInFlightRef.current = false;
      if (opts.initial) {
        setLoadingList(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const loadDetail = async (id, opts = { initial: false }) => {
    if (!id) return;
    if (opts.initial) {
      setLoadingDetail(true);
    }
    try {
      const res = await api.get(`admin/support-chat/conversations/${id}`);
      setDetail(res?.data?.data || null);
      setHours(res?.data?.support_hours || hours);
      // Clear stale error banner after a successful detail fetch.
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load chat details.");
    } finally {
      if (opts.initial) {
        setLoadingDetail(false);
      }
    }
  };

  useEffect(() => {
    loadList({ initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId, { initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    const id = setInterval(async () => {
      // Background refresh (do not block UI / selection)
      await loadList({ initial: false });
      if (selectedId) {
        await loadDetail(selectedId, { initial: false });
      }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const assignToMe = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/assign`);
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to assign conversation.");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/reply`, {
        message: reply.trim(),
      });
      setReply("");
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const closeConversation = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/close`);
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to close conversation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Support Chat Inbox
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {hours?.open
          ? "Live support hours are open."
          : `Live support is outside working hours. ${hours?.label || ""}`}
      </Typography>
      {refreshing ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Refreshing…
        </Typography>
      ) : null}
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ width: { xs: "100%", md: 360 }, maxHeight: 700, overflow: "auto" }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Open Conversations
            </Typography>
          </Box>
          {loadingList ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <List disablePadding>
              {conversations.map((c) => (
                <React.Fragment key={c.id}>
                  <ListItemButton
                    selected={selectedId === c.id}
                    onClick={() => setSelectedId(c.id)}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <ListItemText
                      primary={`#${c.id} ${c.visitor_name || c.visitor_email || "Visitor"}`}
                      secondary={
                        <>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              size="small"
                              color={statusColor(c.status)}
                              label={c.status}
                              sx={{ mr: 1 }}
                            />
                          </Box>
                          <Typography variant="caption" display="block" sx={{ mt: 0.8 }}>
                            {c.escalation_reason || "No escalation reason"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Updated: {formatWhen(c.updated_at)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
              {conversations.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No open conversations right now.
                  </Typography>
                </Box>
              ) : null}
            </List>
          )}
        </Paper>

        <Paper sx={{ flex: 1, minHeight: 700, display: "flex", flexDirection: "column" }}>
          {!selectedId ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Select a conversation from the left.
              </Typography>
            </Box>
          ) : loadingDetail ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Conversation #{detail?.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail?.visitor_user
                    ? `${detail.visitor_user.name} (${detail.visitor_user.email})`
                    : [detail?.visitor_name || "Visitor", detail?.visitor_email ? `(${detail.visitor_email})` : null]
                        .filter(Boolean)
                        .join(" ")}
                  {detail?.visitor_phone ? ` · ${detail.visitor_phone}` : ""}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" color={statusColor(detail?.status)} label={detail?.status || "-"} />
                  {detail?.assigned_admin ? (
                    <Chip size="small" label={`Assigned: ${detail.assigned_admin.name}`} />
                  ) : (
                    <Chip size="small" variant="outlined" label="Unassigned" />
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                {(detail?.messages || []).map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      justifyContent: m.sender_type === "admin" ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "85%",
                        px: 1.2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor:
                          m.sender_type === "visitor"
                            ? "grey.200"
                            : m.sender_type === "admin"
                              ? "primary.main"
                              : "info.light",
                        color: m.sender_type === "admin" ? "primary.contrastText" : "text.primary",
                      }}
                    >
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
                        {m.sender_type}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {m.body}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.3, opacity: 0.7 }}>
                        {formatWhen(m.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={assignToMe}
                    disabled={saving || detail?.status !== "queued_for_human"}
                  >
                    Assign to me
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={closeConversation}
                    disabled={saving || detail?.status === "closed" || !detail?.assigned_admin}
                  >
                    Close
                  </Button>
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Type your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  disabled={detail?.status === "closed"}
                />
                <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={sendReply}
                    disabled={saving || !reply.trim() || detail?.status === "closed"}
                  >
                    Send reply
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
